<script setup>
/* global axios */
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ConversationAction from 'dashboard/routes/dashboard/conversation/ConversationAction.vue';
import CustomAttributes from 'dashboard/routes/dashboard/conversation/customAttributes/CustomAttributes.vue';
import ContactDeals from 'dashboard/components-next/Contacts/ContactsSidebar/ContactDeals.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationInsightPanel from 'dashboard/components-next/captain/brain/ConversationInsightPanel.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import EditContact from 'dashboard/routes/dashboard/conversation/contact/EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { sourceIcon } from 'dashboard/routes/dashboard/crm/composables/useAttribution';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { isEnterprise } = useConfig();

const effectiveChat = useMapGetter('unifiedView/getEffectiveChat');
const currentAccountId = useMapGetter('getCurrentAccountId');

// ── Active Inboxes (unified view) ──
const contactChannels = useMapGetter('unifiedView/getContactChannels');
const isAllTabActive = useMapGetter('unifiedView/isAllTabActive');
const hasActiveInboxes = computed(() => contactChannels.value.length > 0);
const currentInboxId = computed(() => effectiveChat.value?.inbox_id);

const activateAllConversations = () => {
  store.dispatch('unifiedView/switchToAllTab');
};

const navigateToInboxConversation = channel => {
  store.dispatch('unifiedView/exitAllTab');
  router.push({
    name: 'conversation_through_inbox',
    params: {
      accountId: currentAccountId.value,
      inbox_id: channel.inboxId,
      conversation_id: channel.conversationId,
    },
  });
};

// Tab state: 0 = Conversa, 1 = CRM, 2 = brain
const activeTab = ref(0);
// Conversa sub-tab: 'contact' | 'attribution' | 'actions'
const conversaSubTab = ref('contact');
// Brain sub-tab state: 'copilot' | 'insights'
const brainSubTab = ref('copilot');

// ── Contact data ──
const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => effectiveChat.value?.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value) || {});

const contactName = computed(() => contact.value.name || '');
const contactEmail = computed(() => contact.value.email || '');
const contactPhone = computed(() => contact.value.phone_number || '');
const contactAvatar = computed(() => contact.value.thumbnail || '');
const contactStatus = computed(
  () => contact.value.availability_status || 'offline'
);
const contactCity = computed(
  () => contact.value.additional_attributes?.city || ''
);
const contactCompany = computed(
  () =>
    contact.value.company?.name ||
    contact.value.additional_attributes?.company_name ||
    ''
);

// ── Group data ──
const isGroup = computed(() => effectiveChat.value?.is_group === true);
const groupName = computed(
  () => effectiveChat.value?.additional_attributes?.group_name || ''
);
const groupJid = computed(() => effectiveChat.value?.group_jid || '');
const groupAvatar = computed(
  () => effectiveChat.value?.additional_attributes?.group_avatar || ''
);
const groupMembers = ref([]);
const isLoadingMembers = ref(false);
const groupInviteLink = ref('');
const isEditingGroupName = ref(false);
const editGroupNameValue = ref('');
const showAddMemberInput = ref(false);
const addMemberPhone = ref('');

const fetchGroupMembers = async () => {
  if (!isGroup.value || !groupJid.value) return;
  const inboxId = effectiveChat.value?.inbox_id;
  if (!inboxId) return;

  isLoadingMembers.value = true;
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_participants`,
      { group_jid: groupJid.value }
    );
    groupMembers.value = data.participants || [];
  } catch {
    groupMembers.value = [];
  } finally {
    isLoadingMembers.value = false;
  }
};

const fetchInviteLink = async () => {
  if (!isGroup.value || !groupJid.value) return;
  const inboxId = effectiveChat.value?.inbox_id;
  if (!inboxId) return;

  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_invite_link`,
      { group_jid: groupJid.value }
    );
    groupInviteLink.value = data.link || '';
  } catch {
    groupInviteLink.value = '';
  }
};

const copyInviteLink = async () => {
  if (!groupInviteLink.value) return;
  try {
    await navigator.clipboard.writeText(groupInviteLink.value);
    useAlert(t('CONVERSATION.GROUP.INVITE_LINK_COPIED'));
  } catch {
    // clipboard not available
  }
};

const startEditGroupName = () => {
  editGroupNameValue.value = groupName.value;
  isEditingGroupName.value = true;
};

const saveGroupName = async () => {
  if (!editGroupNameValue.value.trim()) return;
  const inboxId = effectiveChat.value?.inbox_id;
  try {
    await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_set_name`,
      { group_jid: groupJid.value, name: editGroupNameValue.value.trim() }
    );
    // Update local conversation
    const attrs = {
      ...(effectiveChat.value?.additional_attributes || {}),
      group_name: editGroupNameValue.value.trim(),
    };
    store.dispatch('updateConversationCustomAttributes', {
      conversationId: effectiveChat.value?.id,
      custom_attributes: attrs,
    });
    isEditingGroupName.value = false;
    useAlert(t('CONVERSATION.GROUP.RENAMED'));
  } catch {
    useAlert(t('CONVERSATION.GROUP.RENAME_ERROR'));
  }
};

const addMember = async () => {
  const phone = addMemberPhone.value.trim().replace(/\D/g, '');
  if (!phone) return;
  const inboxId = effectiveChat.value?.inbox_id;
  try {
    await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_add_participant`,
      { group_jid: groupJid.value, phones: [phone] }
    );
    addMemberPhone.value = '';
    showAddMemberInput.value = false;
    fetchGroupMembers();
    useAlert(t('CONVERSATION.GROUP.MEMBER_ADDED'));
  } catch {
    useAlert(t('CONVERSATION.GROUP.MEMBER_ADD_ERROR'));
  }
};

const removeMember = async member => {
  const phone = member.JID || member.jid;
  if (!phone) return;
  const inboxId = effectiveChat.value?.inbox_id;
  try {
    await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/group_remove_participant`,
      { group_jid: groupJid.value, phones: [phone] }
    );
    fetchGroupMembers();
    useAlert(t('CONVERSATION.GROUP.MEMBER_REMOVED'));
  } catch {
    useAlert(t('CONVERSATION.GROUP.MEMBER_REMOVE_ERROR'));
  }
};

const openingMember = ref(null);

const openMemberConversation = async member => {
  if (!member.phone) return;
  if (openingMember.value) return;

  const jid = member.JID || member.jid;
  openingMember.value = jid;
  try {
    const inboxId = effectiveChat.value?.inbox_id;
    const { data } = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/open_member_conversation`,
      { jid, phone: member.phone, push_name: member.push_name }
    );
    if (data.conversation_id) {
      router.push({
        name: 'conversation_through_inbox',
        params: {
          accountId: currentAccountId.value,
          inbox_id: inboxId,
          conversation_id: data.conversation_id,
        },
      });
    } else {
      useAlert(t('CONVERSATION_SIDEBAR.OPEN_MEMBER_FAILED'));
    }
  } catch {
    useAlert(t('CONVERSATION_SIDEBAR.OPEN_MEMBER_FAILED'));
  } finally {
    openingMember.value = null;
  }
};

const memberTooltip = member => {
  if (!member.phone) return t('CONVERSATION_SIDEBAR.MEMBER_TOOLTIP_NO_PHONE');
  if (member.contact_id) return t('CONVERSATION_SIDEBAR.MEMBER_TOOLTIP_OPEN');
  return t('CONVERSATION_SIDEBAR.MEMBER_TOOLTIP_CREATE');
};

const memberPhone = member => {
  // Prefer resolved phone from LidResolver (set by enrich_participants)
  if (member.phone) return member.phone;
  // Fallback: extract from JID if it's a phone JID (not LID)
  const jid = member.JID || member.jid || '';
  if (jid.includes('@lid')) return null;
  const local = jid.split('@')[0]?.split(':')[0];
  return local && local.length >= 8 ? local : null;
};

// BR mobile numbers with DDD may come with 12 digits (missing 9th digit after DDD).
// Normalize to 13 digits by inserting 9 after DDD for celular numbers.
const normalizeBrazilPhone = raw => {
  const digits = raw.replace(/\D/g, '');
  // 12-digit BR: 55 + 2-digit DDD + 8-digit number → insert 9 after DDD
  if (digits.length === 12 && digits.startsWith('55')) {
    const ddd = digits.slice(2, 4);
    const number = digits.slice(4);
    // Mobile numbers start with 6-9 after DDD
    if (/^[6-9]/.test(number)) {
      return `55${ddd}9${number}`;
    }
  }
  return digits;
};

const formatMemberPhone = phone => {
  if (!phone) return null;
  const normalized = normalizeBrazilPhone(phone);
  const withPlus = `+${normalized}`;
  try {
    const parsed = parsePhoneNumber(withPlus);
    if (parsed?.isValid()) return parsed.formatInternational();
    // Fallback: manual split
    if (normalized.length === 13 && normalized.startsWith('55')) {
      const ddd = normalized.slice(2, 4);
      const rest = normalized.slice(4);
      return `+55 ${ddd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
    }
    return withPlus;
  } catch {
    return withPlus;
  }
};

const memberIsAdmin = member => {
  return (
    member.IsAdmin === true ||
    member.isAdmin === true ||
    member.IsSuperAdmin === true ||
    member.isSuperAdmin === true
  );
};

// ── Attribution data ──
const firstTouch = computed(() => contact.value.first_touch_attribution || {});
const latestTouch = computed(
  () => contact.value.latest_touch_attribution || {}
);
const hasAttribution = computed(
  () =>
    firstTouch.value.utm_source ||
    firstTouch.value.utm_medium ||
    latestTouch.value.utm_source ||
    latestTouch.value.utm_medium
);

const statusColor = computed(() => {
  const map = {
    online: 'bg-green-500',
    busy: 'bg-amber-500',
    offline: 'bg-n-slate-8',
  };
  return map[contactStatus.value] || 'bg-n-slate-8';
});

// ── Conversation ──
const conversationId = computed(
  () => effectiveChat.value?.id || props.currentChat.id
);

// ── CRM: Deal click → open in new tab ──
const currentUser = useMapGetter('getCurrentUser');

const memberAvatarUrl = member => {
  const rawJid = member.JID || member.jid;
  if (!rawJid) {
    return '';
  }
  // Strip device suffix (:NN) to match the cache key used by the
  // backend participant_avatar endpoint
  const normalizedJid = rawJid.replace(/:\d+@/, '@');
  const inboxId = effectiveChat.value?.inbox_id;
  const token = currentUser.value?.access_token;

  if (!inboxId || !token) return '';
  return `/api/v1/accounts/${currentAccountId.value}/inboxes/${inboxId}/dinastia_whatsapp/participant_avatar?jid=${encodeURIComponent(normalizedJid)}&api_access_token=${token}`;
};

const onDealClick = deal => {
  const resolved = router.resolve({
    name: 'crm_item_detail',
    params: { accountId: currentAccountId.value, itemId: deal.id },
  });
  window.open(resolved.href, '_blank');
};

// ── CRM: Create deal ──
const showCreateDealForm = ref(false);
const newDealTitle = ref('');
const crmPipelines = ref([]);
const selectedPipelineId = ref(null);
const isCreatingDeal = ref(false);
const dealListKey = ref(0);
const justCreatedDealId = ref(null);

const fetchPipelines = async () => {
  try {
    const response = await axios.get(
      `/api/v1/accounts/${currentAccountId.value}/pipelines`
    );
    crmPipelines.value = response.data?.payload || response.data || [];
    if (crmPipelines.value.length) {
      selectedPipelineId.value = crmPipelines.value[0].id;
    }
  } catch {
    crmPipelines.value = [];
  }
};

const openCreateDealForm = async () => {
  if (!crmPipelines.value.length) {
    await fetchPipelines();
  }
  if (!crmPipelines.value.length) {
    useAlert(t('CONVERSATION_SIDEBAR.CRM_NO_PIPELINES'));
    return;
  }
  newDealTitle.value = `Deal - ${contactName.value || t('CONVERSATION_SIDEBAR.CRM_NEW')}`;
  showCreateDealForm.value = true;
};

const createDeal = async () => {
  if (!newDealTitle.value.trim()) return;
  isCreatingDeal.value = true;
  try {
    const pipeline = crmPipelines.value.find(
      p => p.id === selectedPipelineId.value
    );
    const firstStage = pipeline?.stages?.[0] || pipeline?.pipeline_stages?.[0];

    const response = await axios.post(
      `/api/v1/accounts/${currentAccountId.value}/pipelines/${selectedPipelineId.value}/deals`,
      {
        deal: {
          title: newDealTitle.value.trim(),
          contact_ids: contactId.value ? [contactId.value] : [],
          agent_ids: currentUser.value?.id ? [currentUser.value.id] : [],
          current_stage_id: firstStage?.id,
        },
      }
    );
    justCreatedDealId.value = response.data?.id || null;
    useAlert(t('CONVERSATION_SIDEBAR.CRM_DEAL_CREATED'));
    showCreateDealForm.value = false;
    newDealTitle.value = '';
    dealListKey.value += 1;
    setTimeout(() => {
      justCreatedDealId.value = null;
    }, 1800);
  } catch {
    useAlert(t('CONVERSATION_SIDEBAR.CRM_DEAL_ERROR'));
  } finally {
    isCreatingDeal.value = false;
  }
};

// ── brain (Copilot) ──
const assistants = useMapGetter('captainAssistants/getRecords');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const selectedCopilotThreadId = ref(null);
const messages = computed(() =>
  store.getters['copilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  )
);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_captain_assistant_id;
  if (preferredId) {
    const found = assistants.value.find(a => a.id === preferredId);
    if (found) return found;
  }
  if (inboxAssistant.value) {
    const found = assistants.value.find(a => a.id === inboxAssistant.value.id);
    if (found) return found;
  }
  return assistants.value[0];
});

const isCaptainAvailable = computed(() => {
  if (!isEnterprise) return false;
  return isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CAPTAIN
  );
});

const sendMessage = async message => {
  if (!message?.trim()) return;
  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('copilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: effectiveChat.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: effectiveChat.value?.id,
        message,
      });
      selectedCopilotThreadId.value = response.id;
    }
  } catch (error) {
    useAlert(error.message);
  }
};

// ── Contact action modals ──
const showEditModal = ref(false);
const showMergeModal = ref(false);

const toggleEditModal = () => {
  showEditModal.value = !showEditModal.value;
};

const openComposeConversationModal = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const closeComposeConversationModal = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const openMergeModal = () => {
  showMergeModal.value = true;
};

const closeMergeModal = () => {
  showMergeModal.value = false;
};

// ── Data loading ──
// Fetch contact data only when contact changes (not on tab change)
watch(
  contactId,
  newId => {
    if (newId) {
      store.dispatch('contacts/show', { id: newId });
      store.dispatch('unifiedView/initForContact', newId);
    } else {
      store.dispatch('unifiedView/clear');
    }
    if (isGroup.value) {
      fetchGroupMembers();
      fetchInviteLink();
    }
  },
  { immediate: true }
);

// Re-fetch group data when conversation changes (component is reused, not remounted)
watch(
  () => effectiveChat.value?.id,
  () => {
    if (isGroup.value) {
      groupMembers.value = [];
      fetchGroupMembers();
      fetchInviteLink();
    } else {
      groupMembers.value = [];
      groupInviteLink.value = '';
    }
  }
);

onMounted(() => {
  store.dispatch('attributes/get', 0);
  if (isEnterprise) {
    store.dispatch('captainAssistants/get');
  }
});

// ── Sync sidebar tab with UI settings (SidepanelSwitch / CopilotMenuBar) ──
// External trigger: sparkle button or "Ask Copilot" sets is_copilot_panel_open
watch(
  () => uiSettings.value.is_copilot_panel_open,
  isOpen => {
    if (isOpen && isCaptainAvailable.value && activeTab.value !== 2) {
      activeTab.value = 2;
      brainSubTab.value = 'copilot';
    } else if (!isOpen && activeTab.value === 2) {
      activeTab.value = 0;
    }
  }
);

// Internal trigger: user clicks tab directly in sidebar → sync back
watch(activeTab, tab => {
  const copilotOpen = uiSettings.value.is_copilot_panel_open;
  if (tab === 2 && !copilotOpen) {
    updateUISettings({
      is_copilot_panel_open: true,
      is_contact_sidebar_open: false,
    });
  } else if (tab !== 2 && copilotOpen) {
    updateUISettings({ is_copilot_panel_open: false });
  }
});

// Force activeTab back to Conversa when switching to group (CRM tab hidden for groups)
watch(
  () => isGroup.value,
  nowGroup => {
    if (nowGroup && activeTab.value === 1) {
      activeTab.value = 0;
    }
  }
);
</script>

<template>
  <div
    class="bg-n-stone-25 dark:bg-n-solid-1 h-full overflow-hidden flex flex-col w-[300px] xl:w-[340px] 2xl:w-[412px] min-w-[280px] xl:min-w-[340px] 2xl:min-w-[412px] flex-shrink-0"
  >
    <!-- Tab Headers -->
    <div class="flex bg-white dark:bg-n-solid-3 flex-shrink-0 h-[48px]">
      <button
        class="flex-1 flex items-center justify-center gap-1.5 px-2 text-[11px] font-semibold transition-colors cursor-pointer border-b-2"
        :class="
          activeTab === 0
            ? 'text-n-slate-12 border-n-brand'
            : 'text-n-slate-10 hover:text-n-slate-12 border-transparent'
        "
        @click="activeTab = 0"
      >
        <span class="i-lucide-message-circle size-3.5" />
        <span>{{ $t('CONVERSATION_SIDEBAR.TAB_CONVERSATION') }}</span>
      </button>
      <button
        v-if="!isGroup"
        class="flex-1 flex items-center justify-center gap-1.5 px-2 text-[11px] font-semibold transition-colors cursor-pointer border-b-2"
        :class="
          activeTab === 1
            ? 'text-n-slate-12 border-n-brand'
            : 'text-n-slate-10 hover:text-n-slate-12 border-transparent'
        "
        @click="activeTab = 1"
      >
        <span class="i-lucide-kanban size-3.5" />
        <span>{{ $t('CONVERSATION_SIDEBAR.TAB_CRM') }}</span>
      </button>
      <button
        v-if="isCaptainAvailable"
        class="flex-1 flex items-center justify-center gap-1.5 px-2 text-[11px] font-semibold transition-colors cursor-pointer border-b-2"
        :class="
          activeTab === 2
            ? 'text-n-slate-12 border-n-brand'
            : 'text-n-slate-10 hover:text-n-slate-12 border-transparent'
        "
        @click="activeTab = 2"
      >
        <span class="i-ph-sparkle-fill size-3.5" />
        <!-- eslint-disable vue/no-bare-strings-in-template -->
        <span class="brain-wordmark"
          >br<span class="brain-highlight">ai</span>n</span
        >
        <!-- eslint-enable vue/no-bare-strings-in-template -->
      </button>
    </div>

    <!-- Tab Content -->
    <div class="flex-1 overflow-hidden">
      <!-- ═══ CONVERSA TAB ═══ -->
      <div v-show="activeTab === 0" class="h-full flex flex-col">
        <!-- Group Header card -->
        <div
          v-if="isGroup"
          class="sidebar-card flex items-center gap-3 px-4 py-3 flex-shrink-0"
        >
          <Avatar
            v-if="groupAvatar"
            :src="groupAvatar"
            :name="groupName"
            :size="36"
            rounded-full
          />
          <div
            v-else
            class="flex items-center justify-center size-9 rounded-full bg-n-teal-3 shrink-0"
          >
            <span class="i-lucide-users size-5 text-n-teal-11" />
          </div>
          <div class="flex-1 min-w-0">
            <span
              class="text-[13px] font-semibold text-n-slate-12 truncate block"
            >
              {{ groupName || groupJid }}
            </span>
            <span class="text-[11px] text-n-slate-10">
              {{ groupMembers.length }}
              {{ $t('CONVERSATION.GROUP.MEMBERS') }}
            </span>
          </div>
          <button
            v-if="groupInviteLink"
            v-tooltip="$t('CONVERSATION.GROUP.COPY_INVITE_LINK')"
            class="size-7 rounded-md hover:bg-n-alpha-1 grid place-content-center text-n-slate-10 hover:text-n-slate-12 transition-colors"
            @click="copyInviteLink"
          >
            <span class="i-lucide-link size-3.5" />
          </button>
        </div>
        <!-- Contact Header card -->
        <div
          v-else
          class="sidebar-card flex items-center gap-3 px-4 py-3 flex-shrink-0"
        >
          <Avatar
            :src="contactAvatar"
            :name="contactName"
            :status="contactStatus"
            :size="36"
            rounded-full
          />
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-1.5">
              <span class="text-[13px] font-semibold text-n-slate-12 truncate">
                {{ contactName }}
              </span>
              <a
                :href="`/app/accounts/${currentAccountId}/contacts/${contactId}`"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-slate-9 hover:text-n-brand transition-colors flex-shrink-0"
                :title="$t('CONVERSATION.HEADER.OPEN_IN_NEW_TAB')"
              >
                <span class="i-lucide-external-link size-3" />
              </a>
            </div>
            <div class="flex items-center gap-2 mt-0.5">
              <span
                v-if="contactPhone"
                class="text-[11px] text-n-slate-10 truncate"
              >
                {{ contactPhone }}
              </span>
              <span
                class="size-1.5 rounded-full flex-shrink-0"
                :class="statusColor"
              />
            </div>
          </div>
          <!-- Contact action buttons -->
          <div class="flex items-center gap-1 flex-shrink-0">
            <ComposeConversation
              :contact-id="String(contactId)"
              is-modal
              @close="closeComposeConversationModal"
            >
              <template #trigger="{ toggle }">
                <NextButton
                  v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
                  icon="i-ph-chat-circle-dots"
                  slate
                  faded
                  xs
                  @click="openComposeConversationModal(toggle)"
                />
              </template>
            </ComposeConversation>
            <NextButton
              v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
              icon="i-ph-pencil-simple"
              slate
              faded
              xs
              @click="toggleEditModal"
            />
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
              icon="i-ph-arrows-merge"
              slate
              faded
              xs
              @click="openMergeModal"
            />
          </div>
        </div>
        <!-- Contact action modals -->
        <EditContact
          v-if="showEditModal"
          :show="showEditModal"
          :contact="contact"
          @cancel="toggleEditModal"
        />
        <ContactMergeModal
          v-if="showMergeModal"
          :primary-contact="contact"
          :show="showMergeModal"
          @close="closeMergeModal"
        />

        <!-- Sub-tab icons -->
        <div class="flex bg-white dark:bg-n-solid-3 flex-shrink-0">
          <button
            class="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-semibold transition-colors cursor-pointer border-b-2"
            :class="
              conversaSubTab === 'contact'
                ? 'text-n-slate-12 border-n-brand'
                : 'text-n-slate-9 hover:text-n-slate-12 border-transparent'
            "
            @click="conversaSubTab = 'contact'"
          >
            <span
              :class="
                isGroup ? 'i-lucide-users size-3.5' : 'i-lucide-user size-3.5'
              "
            />
            <span>{{
              isGroup
                ? $t('CONVERSATION_SIDEBAR.GROUP')
                : $t('CONVERSATION_SIDEBAR.CONTACT')
            }}</span>
          </button>
          <button
            class="flex-1 flex items-center justify-center gap-1 py-2 text-[10px] font-semibold transition-colors cursor-pointer border-b-2"
            :class="
              conversaSubTab === 'actions'
                ? 'text-n-slate-12 border-n-brand'
                : 'text-n-slate-9 hover:text-n-slate-12 border-transparent'
            "
            @click="conversaSubTab = 'actions'"
          >
            <span class="i-lucide-settings-2 size-3.5" />
            <span>{{ $t('CONVERSATION_SIDEBAR.ACTIONS') }}</span>
          </button>
        </div>

        <!-- Sub-tab content -->
        <div class="flex-1 overflow-y-auto slim-scrollbar pt-2">
          <!-- ── CONTACT sub-tab ── -->
          <div
            v-show="conversaSubTab === 'contact'"
            class="flex flex-col gap-2 min-h-full"
          >
            <!-- Group Members panel -->
            <div v-if="isGroup" class="sidebar-card px-4 py-3">
              <div class="section-label mb-2">
                <span class="i-lucide-users size-3" />
                <span>{{ $t('CONVERSATION.GROUP.MEMBERS_TITLE') }}</span>
                <span class="ml-auto text-[10px] text-n-slate-9 font-normal">
                  {{ groupMembers.length }}
                </span>
              </div>
              <div v-if="isLoadingMembers" class="flex justify-center py-4">
                <span class="text-xs text-n-slate-10">...</span>
              </div>
              <div v-else class="flex flex-col gap-1">
                <div
                  v-for="member in groupMembers"
                  :key="member.JID || member.jid"
                  v-tooltip="memberTooltip(member)"
                  class="group/member relative flex items-center gap-2 py-1.5 px-1 rounded-md transition-colors"
                  :class="[
                    member.phone
                      ? 'cursor-pointer hover:bg-n-alpha-2'
                      : 'cursor-not-allowed opacity-60',
                    openingMember === (member.JID || member.jid)
                      ? 'opacity-50 pointer-events-none'
                      : '',
                  ]"
                  @click="openMemberConversation(member)"
                >
                  <div
                    class="size-6 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                    :class="
                      memberIsAdmin(member) ? 'bg-n-amber-3' : 'bg-n-slate-3'
                    "
                  >
                    <img
                      v-if="memberAvatarUrl(member)"
                      :src="memberAvatarUrl(member)"
                      class="size-6 rounded-full object-cover"
                      @error="
                        $event.target.style.display = 'none';
                        $event.target.nextElementSibling?.classList.remove(
                          'hidden'
                        );
                      "
                    />
                    <span
                      :class="[
                        memberIsAdmin(member)
                          ? 'i-lucide-crown text-n-amber-11'
                          : 'i-lucide-user text-n-slate-9',
                        memberAvatarUrl(member) ? 'hidden' : '',
                      ]"
                      class="size-3"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <span class="text-xs text-n-slate-12 block truncate">
                      {{
                        member.name ||
                        member.push_name ||
                        (memberPhone(member)
                          ? formatMemberPhone(memberPhone(member))
                          : $t('CONVERSATION.GROUP.UNKNOWN_PARTICIPANT'))
                      }}
                    </span>
                    <span
                      v-if="
                        memberPhone(member) && (member.name || member.push_name)
                      "
                      class="text-[10px] text-n-slate-9 block truncate"
                    >
                      {{ formatMemberPhone(memberPhone(member)) }}
                    </span>
                  </div>
                  <span
                    v-if="memberIsAdmin(member)"
                    class="text-[10px] text-n-amber-11 font-medium shrink-0"
                  >
                    {{ 'Admin' }}
                  </span>
                  <button
                    v-if="!memberIsAdmin(member)"
                    v-tooltip="$t('CONVERSATION.GROUP.REMOVE_MEMBER')"
                    class="size-5 rounded hover:bg-n-ruby-2 grid place-content-center text-n-slate-9 hover:text-n-ruby-11 transition-colors opacity-0 group-hover/member:opacity-100 shrink-0"
                    @click.stop="removeMember(member)"
                  >
                    <span class="i-lucide-x size-3" />
                  </button>
                  <span
                    v-if="openingMember === (member.JID || member.jid)"
                    class="i-lucide-loader-2 size-3.5 text-n-brand animate-spin shrink-0"
                  />
                </div>
              </div>
              <button
                v-if="groupInviteLink"
                class="flex items-center gap-1.5 mt-3 text-xs text-n-brand hover:underline"
                @click="copyInviteLink"
              >
                <span class="i-lucide-link size-3" />
                {{ $t('CONVERSATION.GROUP.COPY_INVITE_LINK') }}
              </button>
            </div>
            <!-- Group Actions card -->
            <div v-if="isGroup" class="sidebar-card px-4 py-3">
              <div class="section-label mb-2">
                <span class="i-lucide-settings size-3" />
                <span>{{ $t('CONVERSATION.GROUP.ACTIONS_TITLE') }}</span>
              </div>
              <div class="flex flex-col gap-1.5">
                <button
                  class="flex items-center gap-2 px-2 py-1.5 text-xs text-n-slate-11 rounded-md hover:bg-n-alpha-1 transition-colors w-full text-left"
                  @click="startEditGroupName"
                >
                  <span class="i-lucide-pencil size-3.5" />
                  {{ $t('CONVERSATION.GROUP.RENAME') }}
                </button>
                <button
                  class="flex items-center gap-2 px-2 py-1.5 text-xs text-n-slate-11 rounded-md hover:bg-n-alpha-1 transition-colors w-full text-left"
                  @click="showAddMemberInput = !showAddMemberInput"
                >
                  <span class="i-lucide-user-plus size-3.5" />
                  {{ $t('CONVERSATION.GROUP.ADD_MEMBER') }}
                </button>
                <button
                  v-if="groupInviteLink"
                  class="flex items-center gap-2 px-2 py-1.5 text-xs text-n-slate-11 rounded-md hover:bg-n-alpha-1 transition-colors w-full text-left"
                  @click="copyInviteLink"
                >
                  <span class="i-lucide-link size-3.5" />
                  {{ $t('CONVERSATION.GROUP.COPY_INVITE_LINK') }}
                </button>
              </div>
              <!-- Inline rename -->
              <div v-if="isEditingGroupName" class="mt-3">
                <input
                  v-model="editGroupNameValue"
                  type="text"
                  class="w-full text-xs border border-n-weak rounded-md px-2.5 py-1.5 bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                  @keydown.enter="saveGroupName"
                  @keydown.escape="isEditingGroupName = false"
                />
                <div class="flex gap-1.5 mt-1.5">
                  <button
                    class="px-2.5 py-1 text-[11px] font-medium bg-n-brand text-white rounded-md"
                    @click="saveGroupName"
                  >
                    {{ $t('CONVERSATION.GROUP.SAVE') }}
                  </button>
                  <button
                    class="px-2.5 py-1 text-[11px] text-n-slate-10 hover:text-n-slate-12"
                    @click="isEditingGroupName = false"
                  >
                    {{ $t('CONVERSATION.GROUP.CANCEL') }}
                  </button>
                </div>
              </div>
              <!-- Inline add member -->
              <div v-if="showAddMemberInput" class="mt-3">
                <input
                  v-model="addMemberPhone"
                  type="text"
                  :placeholder="$t('CONVERSATION.GROUP.ADD_MEMBER_PLACEHOLDER')"
                  class="w-full text-xs border border-n-weak rounded-md px-2.5 py-1.5 bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand"
                  @keydown.enter="addMember"
                />
                <div class="flex gap-1.5 mt-1.5">
                  <button
                    class="px-2.5 py-1 text-[11px] font-medium bg-n-brand text-white rounded-md"
                    :disabled="!addMemberPhone.trim()"
                    @click="addMember"
                  >
                    {{ $t('CONVERSATION.GROUP.ADD') }}
                  </button>
                  <button
                    class="px-2.5 py-1 text-[11px] text-n-slate-10 hover:text-n-slate-12"
                    @click="showAddMemberInput = false"
                  >
                    {{ $t('CONVERSATION.GROUP.CANCEL') }}
                  </button>
                </div>
              </div>
            </div>
            <!-- Contact info card (hidden for groups) -->
            <div v-if="!isGroup" class="sidebar-card px-4 py-3">
              <div class="section-label mb-2">
                <span class="i-lucide-user size-3" />
                <span>{{ $t('CONVERSATION_SIDEBAR.CONTACT_INFO_TITLE') }}</span>
              </div>
              <div class="info-row">
                <span
                  class="i-lucide-phone size-3.5 text-n-slate-9 flex-shrink-0"
                />
                <span class="info-label">{{
                  $t('CONVERSATION_SIDEBAR.PHONE')
                }}</span>
                <span
                  class="info-value"
                  :class="{ 'text-n-slate-9': !contactPhone }"
                  >{{ contactPhone || '---' }}</span
                >
              </div>
              <div class="info-row">
                <span
                  class="i-lucide-mail size-3.5 text-n-slate-9 flex-shrink-0"
                />
                <span class="info-label">{{
                  $t('CONVERSATION_SIDEBAR.EMAIL')
                }}</span>
                <span
                  class="info-value"
                  :class="{ 'text-n-slate-9': !contactEmail }"
                  >{{ contactEmail || '---' }}</span
                >
              </div>
              <div class="info-row">
                <span
                  class="i-lucide-map-pin size-3.5 text-n-slate-9 flex-shrink-0"
                />
                <span class="info-label">{{
                  $t('CONVERSATION_SIDEBAR.CITY')
                }}</span>
                <span
                  class="info-value"
                  :class="{ 'text-n-slate-9': !contactCity }"
                  >{{ contactCity || '---' }}</span
                >
              </div>
              <div class="info-row">
                <span
                  class="i-lucide-building-2 size-3.5 text-n-slate-9 flex-shrink-0"
                />
                <span class="info-label">{{
                  $t('CONVERSATION_SIDEBAR.COMPANY')
                }}</span>
                <span
                  class="info-value"
                  :class="{ 'text-n-slate-9': !contactCompany }"
                  >{{ contactCompany || '---' }}</span
                >
              </div>
            </div>

            <!-- Attribution card (hidden for groups) -->
            <div v-if="!isGroup" class="sidebar-card px-4 py-3">
              <div class="section-label mb-2">
                <span class="i-lucide-compass size-3" />
                <span>{{ $t('CONVERSATION_SIDEBAR.ATTRIBUTION') }}</span>
              </div>
              <div class="space-y-4">
                <!-- First Touch -->
                <div>
                  <div
                    class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wide mb-2"
                  >
                    {{ $t('CONVERSATION_SIDEBAR.FIRST_TOUCH') }}
                  </div>
                  <div class="space-y-1.5">
                    <div class="flex items-center gap-2">
                      <span
                        :class="
                          firstTouch.utm_source
                            ? sourceIcon(firstTouch.utm_source)
                            : 'i-lucide-globe'
                        "
                        class="size-3.5 text-n-slate-9 flex-shrink-0"
                      />
                      <span class="text-[11px] text-n-slate-10 min-w-[50px]">{{
                        $t('CONVERSATION_SIDEBAR.SOURCE')
                      }}</span>
                      <span
                        class="text-[12px] font-medium"
                        :class="
                          firstTouch.utm_source
                            ? 'text-n-slate-12'
                            : 'text-n-slate-9'
                        "
                        >{{ firstTouch.utm_source || '---' }}</span
                      >
                    </div>
                    <div class="flex items-center gap-2">
                      <span
                        class="i-lucide-layers size-3.5 text-n-slate-9 flex-shrink-0"
                      />
                      <span class="text-[11px] text-n-slate-10 min-w-[50px]">{{
                        $t('CONVERSATION_SIDEBAR.MEDIUM')
                      }}</span>
                      <span
                        class="text-[12px] font-medium"
                        :class="
                          firstTouch.utm_medium
                            ? 'text-n-slate-12'
                            : 'text-n-slate-9'
                        "
                        >{{ firstTouch.utm_medium || '---' }}</span
                      >
                    </div>
                    <div class="flex items-center gap-2">
                      <span
                        class="i-lucide-megaphone size-3.5 text-n-slate-9 flex-shrink-0"
                      />
                      <span class="text-[11px] text-n-slate-10 min-w-[50px]">{{
                        $t('CONVERSATION_SIDEBAR.CAMPAIGN')
                      }}</span>
                      <span
                        class="text-[12px] font-medium truncate"
                        :class="
                          firstTouch.utm_campaign
                            ? 'text-n-slate-12'
                            : 'text-n-slate-9'
                        "
                        >{{ firstTouch.utm_campaign || '---' }}</span
                      >
                    </div>
                    <div class="flex items-center gap-2">
                      <span
                        class="i-lucide-calendar size-3.5 text-n-slate-9 flex-shrink-0"
                      />
                      <span class="text-[11px] text-n-slate-10 min-w-[50px]">{{
                        $t('CONVERSATION_SIDEBAR.DATE')
                      }}</span>
                      <span
                        class="text-[12px] font-medium"
                        :class="
                          firstTouch.timestamp
                            ? 'text-n-slate-12'
                            : 'text-n-slate-9'
                        "
                        >{{
                          firstTouch.timestamp
                            ? new Date(firstTouch.timestamp).toLocaleDateString(
                                'pt-BR'
                              )
                            : '---'
                        }}</span
                      >
                    </div>
                  </div>
                </div>

                <!-- Latest Touch (only if different from first) -->
                <div
                  v-if="
                    hasAttribution &&
                    (latestTouch.utm_source || latestTouch.utm_medium) &&
                    latestTouch.timestamp !== firstTouch.timestamp
                  "
                >
                  <div
                    class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wide mb-2"
                  >
                    {{ $t('CONVERSATION_SIDEBAR.LATEST_TOUCH') }}
                  </div>
                  <div class="space-y-1.5">
                    <div class="flex items-center gap-2">
                      <span
                        :class="
                          latestTouch.utm_source
                            ? sourceIcon(latestTouch.utm_source)
                            : 'i-lucide-globe'
                        "
                        class="size-3.5 text-n-slate-9 flex-shrink-0"
                      />
                      <span class="text-[11px] text-n-slate-10 min-w-[50px]">{{
                        $t('CONVERSATION_SIDEBAR.SOURCE')
                      }}</span>
                      <span
                        class="text-[12px] font-medium"
                        :class="
                          latestTouch.utm_source
                            ? 'text-n-slate-12'
                            : 'text-n-slate-9'
                        "
                        >{{ latestTouch.utm_source || '---' }}</span
                      >
                    </div>
                    <div class="flex items-center gap-2">
                      <span
                        class="i-lucide-layers size-3.5 text-n-slate-9 flex-shrink-0"
                      />
                      <span class="text-[11px] text-n-slate-10 min-w-[50px]">{{
                        $t('CONVERSATION_SIDEBAR.MEDIUM')
                      }}</span>
                      <span
                        class="text-[12px] font-medium"
                        :class="
                          latestTouch.utm_medium
                            ? 'text-n-slate-12'
                            : 'text-n-slate-9'
                        "
                        >{{ latestTouch.utm_medium || '---' }}</span
                      >
                    </div>
                    <div class="flex items-center gap-2">
                      <span
                        class="i-lucide-megaphone size-3.5 text-n-slate-9 flex-shrink-0"
                      />
                      <span class="text-[11px] text-n-slate-10 min-w-[50px]">{{
                        $t('CONVERSATION_SIDEBAR.CAMPAIGN')
                      }}</span>
                      <span
                        class="text-[12px] font-medium truncate"
                        :class="
                          latestTouch.utm_campaign
                            ? 'text-n-slate-12'
                            : 'text-n-slate-9'
                        "
                        >{{ latestTouch.utm_campaign || '---' }}</span
                      >
                    </div>
                    <div class="flex items-center gap-2">
                      <span
                        class="i-lucide-calendar size-3.5 text-n-slate-9 flex-shrink-0"
                      />
                      <span class="text-[11px] text-n-slate-10 min-w-[50px]">{{
                        $t('CONVERSATION_SIDEBAR.DATE')
                      }}</span>
                      <span
                        class="text-[12px] font-medium"
                        :class="
                          latestTouch.timestamp
                            ? 'text-n-slate-12'
                            : 'text-n-slate-9'
                        "
                        >{{
                          latestTouch.timestamp
                            ? new Date(
                                latestTouch.timestamp
                              ).toLocaleDateString('pt-BR')
                            : '---'
                        }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Custom Attributes card (hidden for groups) -->
            <div v-if="!isGroup" class="sidebar-card px-4 py-3">
              <div class="section-label mb-2">
                <span class="i-lucide-sliders-horizontal size-3" />
                <span>{{ $t('CONVERSATION_SIDEBAR.CUSTOM_ATTRIBUTES') }}</span>
              </div>
              <CustomAttributes
                attribute-type="contact_attribute"
                :contact-id="contactId"
                attribute-from="conversation_sidebar"
                :empty-state-message="
                  $t('CONVERSATION_SIDEBAR.NO_CUSTOM_ATTRIBUTES')
                "
              />
            </div>

            <!-- Active Inboxes card (stretches to fill remaining space) -->
            <div v-if="!isGroup" class="sidebar-card px-4 py-3 flex-1">
              <div class="section-label mb-2">
                <span class="i-lucide-inbox size-3" />
                <span>{{ $t('CONVERSATION_SIDEBAR.ACTIVE_INBOXES') }}</span>
              </div>
              <div v-if="hasActiveInboxes" class="flex flex-col gap-0.5">
                <!-- "Todas as conversas" merged timeline -->
                <button
                  class="flex items-center gap-2.5 py-1.5 px-1 -mx-1 rounded-md hover:bg-n-alpha-2 transition-colors text-left w-full"
                  :class="{
                    'bg-n-brand/5 ring-1 ring-n-brand/10': isAllTabActive,
                  }"
                  @click="activateAllConversations"
                >
                  <span
                    class="i-lucide-layers size-3.5 text-n-slate-9 flex-shrink-0"
                  />
                  <span class="text-[12px] font-medium text-n-slate-11 flex-1">
                    {{ $t('CONVERSATION_SIDEBAR.ALL_CONVERSATIONS') }}
                  </span>
                </button>
                <!-- Per-inbox entries -->
                <button
                  v-for="channel in contactChannels"
                  :key="channel.inboxId"
                  class="flex items-center gap-2.5 py-1.5 px-1 -mx-1 rounded-md hover:bg-n-alpha-2 transition-colors text-left w-full"
                  :class="{
                    'bg-n-brand/5 ring-1 ring-n-brand/10':
                      !isAllTabActive && channel.inboxId === currentInboxId,
                  }"
                  @click="navigateToInboxConversation(channel)"
                >
                  <span
                    :class="[channel.icon, channel.colorClass]"
                    class="size-3.5 flex-shrink-0"
                  />
                  <span
                    class="text-[12px] font-medium text-n-slate-12 truncate flex-1"
                  >
                    {{ channel.inboxName }}
                  </span>
                  <span
                    v-if="channel.canReply"
                    v-tooltip="$t('CONVERSATION_SIDEBAR.CAN_CHAT')"
                    class="i-lucide-message-circle size-3 text-n-slate-8 flex-shrink-0"
                  />
                  <span
                    v-else
                    v-tooltip="$t('CONVERSATION_SIDEBAR.VIEW_ONLY')"
                    class="i-lucide-eye size-3 text-n-slate-8 flex-shrink-0"
                  />
                </button>
              </div>
              <div v-else class="text-[11px] text-n-slate-9">
                {{ $t('CONVERSATION_SIDEBAR.NO_ACTIVE_INBOXES') }}
              </div>
            </div>
          </div>

          <!-- ── ACTIONS sub-tab ── -->
          <div v-if="conversaSubTab === 'actions'" class="pb-2 pt-2">
            <div class="sidebar-card px-3 py-2 sidebar-actions">
              <ConversationAction
                v-if="conversationId"
                :conversation-id="conversationId"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ CRM TAB ═══ -->
      <div
        v-show="activeTab === 1"
        class="h-full overflow-y-auto slim-scrollbar pt-2"
      >
        <div class="flex flex-col gap-2 pb-2 min-h-full">
          <!-- Deals card -->
          <div class="sidebar-card">
            <div class="flex items-center justify-between px-4 py-2.5">
              <div class="section-label !mb-0">
                <span class="i-lucide-briefcase size-3" />
                <span>{{ $t('CONVERSATION_SIDEBAR.CRM_DEALS') }}</span>
              </div>
              <NextButton
                xs
                ghost
                icon="i-lucide-plus"
                :label="$t('CONVERSATION_SIDEBAR.CRM_NEW')"
                @click="openCreateDealForm"
              />
            </div>

            <!-- Inline create deal form -->
            <div v-if="showCreateDealForm" class="px-4 pb-3 space-y-2">
              <input
                v-model="newDealTitle"
                type="text"
                class="w-full text-xs rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 placeholder-n-slate-9 outline-none focus:ring-1 focus:ring-n-brand"
                :placeholder="
                  $t('CONVERSATION_SIDEBAR.CRM_DEAL_TITLE_PLACEHOLDER')
                "
                @keydown.enter="createDeal"
                @keydown.escape="showCreateDealForm = false"
              />
              <select
                v-if="crmPipelines.length > 1"
                v-model="selectedPipelineId"
                class="w-full text-xs rounded-lg px-3 py-2 bg-n-alpha-1 text-n-slate-12 outline-none focus:ring-1 focus:ring-n-brand"
              >
                <option v-for="p in crmPipelines" :key="p.id" :value="p.id">
                  {{ p.name }}
                </option>
              </select>
              <div class="flex gap-2">
                <NextButton
                  xs
                  slate
                  faded
                  :label="$t('CONVERSATION_SIDEBAR.CRM_CANCEL')"
                  class="flex-1"
                  @click="showCreateDealForm = false"
                />
                <NextButton
                  xs
                  :label="$t('CONVERSATION_SIDEBAR.CRM_CREATE')"
                  class="flex-1"
                  :is-loading="isCreatingDeal"
                  @click="createDeal"
                />
              </div>
            </div>

            <ContactDeals
              v-if="contactId"
              :key="dealListKey"
              :contact-id="contactId"
              :highlight-deal-id="justCreatedDealId"
              compact
              emit-on-click
              @deal-click="onDealClick"
            />
          </div>

          <!-- Company card -->
          <div class="sidebar-card px-4 py-3">
            <div class="section-label mb-2">
              <span class="i-lucide-building-2 size-3" />
              <span>{{ $t('CONVERSATION_SIDEBAR.CRM_COMPANY') }}</span>
            </div>
            <div v-if="contactCompany" class="flex items-center gap-2.5 py-1">
              <div
                class="size-8 rounded-lg bg-n-alpha-2 flex items-center justify-center text-[13px] font-bold text-n-slate-11"
              >
                {{ contactCompany.charAt(0) }}
              </div>
              <div>
                <div class="text-[13px] font-semibold text-n-slate-12">
                  {{ contactCompany }}
                </div>
              </div>
            </div>
            <div v-else class="text-[11px] text-n-slate-9">
              {{ $t('CONVERSATION_SIDEBAR.CRM_NO_COMPANY') }}
            </div>
          </div>

          <!-- Activity card -->
          <div class="sidebar-card px-4 py-3">
            <div class="section-label mb-2">
              <span class="i-lucide-activity size-3" />
              <span>{{ $t('CONVERSATION_SIDEBAR.CRM_ACTIVITY') }}</span>
            </div>
            <div class="text-[11px] text-n-slate-9">
              {{ $t('CONVERSATION_SIDEBAR.CRM_ACTIVITY_PLACEHOLDER') }}
            </div>
          </div>

          <!-- Tasks card -->
          <div class="sidebar-card px-4 py-3 flex-1">
            <div class="section-label mb-2">
              <span class="i-lucide-check-square size-3" />
              <span>{{ $t('CONVERSATION_SIDEBAR.CRM_TASKS') }}</span>
            </div>
            <div class="text-[11px] text-n-slate-9">
              {{ $t('CONVERSATION_SIDEBAR.CRM_TASKS_PLACEHOLDER') }}
            </div>
          </div>
        </div>
      </div>

      <!-- ═══ BRAIN TAB ═══ -->
      <div
        v-if="isCaptainAvailable"
        v-show="activeTab === 2"
        class="h-full flex flex-col"
      >
        <!-- brain header -->
        <div
          class="flex items-center gap-2.5 px-4 py-3 bg-white dark:bg-n-solid-3"
        >
          <div
            class="size-9 rounded-[10px] bg-gradient-to-br from-n-brand to-n-ruby-9 flex items-center justify-center flex-shrink-0"
          >
            <span class="i-ph-sparkle-fill size-4.5 text-white" />
          </div>
          <div>
            <!-- eslint-disable vue/no-bare-strings-in-template -->
            <div class="text-sm font-bold text-n-slate-12">
              br<span class="brain-highlight">ai</span>n
            </div>
            <!-- eslint-enable vue/no-bare-strings-in-template -->
            <div class="text-[11px] text-n-slate-9">
              {{
                activeAssistant?.name ||
                $t('CONVERSATION_SIDEBAR.BRAIN_ASSISTANT')
              }}
            </div>
          </div>
        </div>

        <!-- Sub-tabs: Copilot | Insights -->
        <div class="flex flex-shrink-0 bg-white dark:bg-n-solid-2">
          <button
            class="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-semibold transition-colors cursor-pointer border-b-2"
            :class="
              brainSubTab === 'copilot'
                ? 'text-n-slate-12 border-n-brand'
                : 'text-n-slate-10 hover:text-n-slate-12 border-transparent'
            "
            @click="brainSubTab = 'copilot'"
          >
            <span class="i-lucide-message-circle size-3.5" />
            <span>{{ $t('CAPTAIN.BRAIN.SIDEBAR_COPILOT') }}</span>
          </button>
          <button
            class="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-[11px] font-semibold transition-colors cursor-pointer border-b-2"
            :class="
              brainSubTab === 'insights'
                ? 'text-n-slate-12 border-n-brand'
                : 'text-n-slate-10 hover:text-n-slate-12 border-transparent'
            "
            @click="brainSubTab = 'insights'"
          >
            <span class="i-lucide-sparkles size-3.5" />
            <span>{{ $t('CAPTAIN.BRAIN.SIDEBAR_INSIGHTS') }}</span>
          </button>
        </div>

        <!-- Copilot sub-tab -->
        <template v-if="brainSubTab === 'copilot'">
          <!-- Copilot messages area -->
          <div class="flex-1 overflow-y-auto sidebar-card !rounded-none">
            <div
              v-if="!messages.length"
              class="flex flex-col items-center justify-center h-full text-center px-4"
            >
              <span class="i-ph-sparkle-fill size-8 text-n-brand/40 mb-3" />
              <p class="text-xs text-n-slate-11 m-0 mb-1">
                {{ $t('CONVERSATION_SIDEBAR.BRAIN_EMPTY_TITLE') }}
              </p>
              <p class="text-[10px] text-n-slate-9 m-0 max-w-[220px]">
                {{ $t('CONVERSATION_SIDEBAR.BRAIN_EMPTY_DESCRIPTION') }}
              </p>
            </div>
            <div v-else class="p-3 space-y-3">
              <div
                v-for="(msg, idx) in messages"
                :key="idx"
                class="text-xs p-2.5 rounded-lg"
                :class="
                  msg.role === 'assistant' ? 'bg-n-brand/5' : 'bg-n-alpha-2'
                "
              >
                {{ msg.content }}
              </div>
            </div>
          </div>

          <!-- Input area -->
          <div class="p-3 mt-auto sidebar-card !rounded-none">
            <div
              class="flex items-center gap-2 bg-n-alpha-1 rounded-[10px] px-3 py-2"
            >
              <input
                type="text"
                class="flex-1 text-xs text-n-slate-12 placeholder-n-slate-9 outline-none bg-transparent"
                :placeholder="
                  $t('CONVERSATION_SIDEBAR.BRAIN_INPUT_PLACEHOLDER')
                "
                @keydown.enter="
                  sendMessage($event.target.value);
                  $event.target.value = '';
                "
              />
              <button
                class="size-6 rounded-md bg-gradient-to-br from-n-brand to-n-ruby-9 text-white grid place-content-center flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
              >
                <span class="i-lucide-send size-3" />
              </button>
            </div>
          </div>
        </template>

        <!-- Insights sub-tab -->
        <ConversationInsightPanel v-else :conversation="effectiveChat" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Card style: white cards on conversation-bg gaps ── */
.sidebar-card {
  background: white;
  border-radius: 0;
}

/* ── Section label (replaces old section-divider) ── */
.section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgb(var(--n-slate-10));
}

.section-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgb(var(--n-slate-9));
  margin-bottom: 8px;
}

/* ── Info rows ── */
.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 0;
}

.info-label {
  font-size: 10px;
  color: rgb(var(--n-slate-9));
  min-width: 55px;
}

.info-value {
  font-size: 12px;
  color: rgb(var(--n-slate-12));
  font-weight: 500;
}

/* ── brain branding ── */
.brain-wordmark {
  font-weight: 700;
  font-size: 11px;
  letter-spacing: -0.3px;
}

.brain-highlight {
  font-weight: 800;
  background: linear-gradient(135deg, rgb(var(--iris-9)), rgb(var(--ruby-9)));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* ── ConversationAction title labels — reduce from text-sm to 12px ── */
.sidebar-actions :deep(.text-sm) {
  font-size: 12px;
}

/* ── ConversationAction spacing & sizing (compact) ── */
.sidebar-actions :deep(.multiselect-wrap--small) {
  margin-bottom: 8px;
  padding-bottom: 8px;
}

.sidebar-actions :deep(.multiselect-wrap--small:last-of-type) {
  margin-bottom: 2px;
  padding-bottom: 0;
}

.sidebar-actions :deep(.multiselect-wrap--small .multiselect) {
  min-height: 30px;
}

.sidebar-actions :deep(.multiselect__tags) {
  min-height: 30px;
  padding: 4px 28px 4px 8px;
  font-size: 11px;
}

.sidebar-actions :deep(.multiselect__select) {
  height: 28px;
}

.sidebar-actions :deep(.multiselect__single),
.sidebar-actions :deep(.multiselect__input) {
  font-size: 11px;
}

.sidebar-actions :deep(.contact--name) {
  font-size: 11px;
}

.sidebar-actions :deep(.conversation--label) {
  font-size: 11px;
}

/* Labels section spacing */
.sidebar-actions :deep(.conversation--label-wrap) {
  margin-top: 8px;
  padding-top: 8px;
}

/* ContactDetailsItem title spacing */
.sidebar-actions :deep(.contact--item) {
  padding-top: 1px;
  padding-bottom: 4px;
}
</style>

<style>
/* Dark mode card background — non-scoped because .dark is on body.
   !important needed to beat the scoped `background: white` which has equal specificity */
.dark .sidebar-card {
  background: rgb(var(--n-solid-3)) !important;
}
</style>
